import React, { useState, useEffect, useRef } from "react";
import { ArrowBack } from "@mui/icons-material";
import { Button, Form } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { useGlobalToast } from "../../../../stopOldToastGlobally";
import Widgets from "../../../../components/Widgets/index";
import { getCapitalizeWord, getInitials, getInitialColor, getCurrentDate, downloadCSV } from "../../../../services/commonServices";
import { HasPermission } from "../../../../services/hasPermission";
import { useDispatch, useSelector } from "react-redux";
import { commonSaveData, initCommonState } from "../../../../redux/slices/commonSlice";

const RunPayroll = () => {
  const customToast = useGlobalToast();
  const location = useLocation();
  const dispatch = useDispatch();
  const commonStore = useSelector((state) => state.common);

  const queryParams = new URLSearchParams(location.search);
  const staffName = queryParams.get("staffName");
  const staffId = queryParams.get("staffId");

  const [selectedMonth, setSelectedMonth] = useState("2023-10");
  const [selectedBranch, setSelectedBranch] = useState("All Branches");
  const [selectedDepartment, setSelectedDepartment] = useState("All Departments");
  const [selectedType, setSelectedType] = useState("All Types");
  const [selectedPayPeriod, setSelectedPayPeriod] = useState("Monthly");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [showPostRun, setShowPostRun] = useState(false);

  // KPI Data
  const kpiData = [
    { icon: "fa-solid fa-wallet", iconColor: "var(--primary)", glow: "rgba(134,96,248,0.12)", label: "Total Payroll", badge: "↑ 2.4%", bc: "status-chip-success", value: "₹1,24,50,000", sub: "vs ₹1,21,58,000 last month" },
    { icon: "fa-solid fa-money-bill-trend-up", iconColor: "var(--color-success)", glow: "rgba(74,222,128,0.10)", label: "Total Gross", badge: "↑ 1.8%", bc: "status-chip-success", value: "₹1,84,20,000", sub: "Before deductions" },
    { icon: "fa-solid fa-hand-holding-dollar", iconColor: "#22d3ee", glow: "rgba(34,211,238,0.10)", label: "Total Net", badge: "↑ 2.1%", bc: "status-chip-success", value: "₹1,48,50,780", sub: "After all deductions" },
    { icon: "fa-solid fa-users", iconColor: "var(--color-warning)", glow: "rgba(245,158,11,0.10)", label: "Employees", badge: "+12", bc: "status-chip-success", value: "842", sub: "Ready for processing" },
    { icon: "fa-solid fa-building-columns", iconColor: "var(--color-success)", glow: "rgba(74,222,128,0.10)", label: "Statutory", badge: "— 0.0%", bc: "status-chip-accent", value: "₹35,69,220", sub: "Taxes, PF, ESI" },
    { icon: "fa-solid fa-triangle-exclamation", iconColor: "var(--color-danger)", glow: "rgba(239,68,68,0.10)", label: "Exceptions", badge: "↓ 3", bc: "status-chip-error", value: "14", sub: "Requires review" },
  ];

  // Checklist Data
  const checklistData = [
    { title: "Attendance Data Locked", sub: "All timesheets approved and frozen", status: "Complete", type: "success", icon: "fa-solid fa-check" },
    { title: "Salary Structures Verified", sub: "All employee pay components validated", status: "Complete", type: "success", icon: "fa-solid fa-check" },
    { title: "Leave & Deductions Applied", sub: "LOP, advances, and loans calculated", status: "Complete", type: "success", icon: "fa-solid fa-check" },
    { title: "Tax Calculations Updated", sub: "TDS computed based on declarations", status: "Complete", type: "success", icon: "fa-solid fa-check" },
    { title: "Bank Account Validation", sub: "All payment methods verified", status: "Complete", type: "success", icon: "fa-solid fa-check" },
    { title: "Exception Resolution", sub: "14 employees require manual review", status: "Review Now", type: "warning", icon: "fa-solid fa-clock", hasButton: true },
  ];

  // Exception Data
  const exceptionData = [
    { icon: "fa-solid fa-credit-card", color: "var(--color-danger)", bg: "rgba(239,68,68,0.1)", title: "Missing Bank Details", count: "5 employees", type: "error" },
    { icon: "fa-solid fa-clock", color: "var(--color-warning)", bg: "rgba(245,158,11,0.1)", title: "Timesheet Discrepancy", count: "6 employees", type: "warning" },
    { icon: "fa-solid fa-file-circle-xmark", color: "var(--color-danger)", bg: "rgba(239,68,68,0.1)", title: "Incomplete Documents", count: "3 employees", type: "error" },
  ];

  // Employee Table Data

// Employee Table Data
const employeeRows = [
  { avatar: "https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-4.jpg", name: "Sarah Jenkins", role: "Sr. Developer", id: "ENG-042", dept: "Engineering", basic: "₹55,000", allowances: "₹39,500", gross: "₹94,500", deductions: "-₹21,450", net: "₹73,050", status: "Ready", statusType: "success" },
  { avatar: "https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-8.jpg", name: "Marcus Chen", role: "Account Exec.", id: "SAL-118", dept: "Sales", basic: "₹42,000", allowances: "₹20,000", gross: "₹62,000", deductions: "-₹14,500", net: "₹47,500", status: "Review", statusType: "warning" },
  { avatar: "https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-5.jpg", name: "Emma Wilson", role: "Marketing Lead", id: "MKT-089", dept: "Marketing", basic: "₹50,000", allowances: "₹28,000", gross: "₹78,000", deductions: "-₹18,900", net: "₹59,100", status: "Hold", statusType: "error" },
  { avatar: "https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-3.jpg", name: "Raj Kumar", role: "Product Mgr", id: "PRD-056", dept: "Product", basic: "₹65,000", allowances: "₹32,000", gross: "₹97,000", deductions: "-₹22,560", net: "₹74,440", status: "Ready", statusType: "success" },
  { avatar: "https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-6.jpg", name: "Lisa Anderson", role: "HR Manager", id: "HR-023", dept: "HR", basic: "₹48,000", allowances: "₹24,000", gross: "₹72,000", deductions: "-₹17,280", net: "₹54,720", status: "Ready", statusType: "success" },
];
  // Insight Data
  const insightData = [
    { icon: "fa-solid fa-bolt", grad: "linear-gradient(135deg, var(--primary), #7c3aed)", shadow: "rgba(134,96,248,0.2)", label: "Avg Processing Time", value: "2.4 hrs", trend: "↓ 18% faster", trendColor: "var(--color-success)" },
    { icon: "fa-solid fa-trophy", grad: "linear-gradient(135deg, var(--color-success), #16a34a)", shadow: "rgba(74,222,128,0.2)", label: "Compliance Score", value: "98.6%", trend: "✓ Excellent", trendColor: "var(--color-success)" },
    { icon: "fa-solid fa-coins", grad: "linear-gradient(135deg, var(--color-warning), #ea580c)", shadow: "rgba(245,158,11,0.2)", label: "Overtime Cost", value: "₹42.8L", trend: "↑ +12% MoM", trendColor: "var(--color-warning)" },
    { icon: "fa-solid fa-percent", grad: "linear-gradient(135deg, #3b82f6, #1d4ed8)", shadow: "rgba(59,130,246,0.2)", label: "Error Rate", value: "0.8%", trend: "↓ -0.3%", trendColor: "var(--color-success)" },
  ];

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedEmployees(employeeRows.map(emp => emp.id));
    } else {
      setSelectedEmployees([]);
    }
  };

  const handleEmployeeSelect = (empId) => {
    if (selectedEmployees.includes(empId)) {
      setSelectedEmployees(selectedEmployees.filter(id => id !== empId));
    } else {
      setSelectedEmployees([...selectedEmployees, empId]);
    }
  };

  const handleRunPayroll = () => {
    setShowPostRun(true);
    setTimeout(() => {
      document.getElementById("post-run-actions")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  return (
    <div style={{ backgroundColor: "transparent", minHeight: "100vh", fontFamily: "var(--font)" }}>
      {/* Back Navigation */}
      <div
        onClick={() => window.location.href = "/payroll"}
        style={{
          textDecoration: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "20px",
          padding: "0 20px"
        }}
        className="color-white"
      >
        {/* <ArrowBack style={{ cursor: "pointer", fontSize: "20px" }} className="color-white" />
        <span style={{ fontSize: "14px" }}>Back to Payroll</span> */}
      </div>

      <main style={{ maxWidth: 1600, margin: "0 auto", padding: "0 24px 24px", display: "flex", flexDirection: "column", gap: 32 }}>

        {/* Header Section */}
        <header style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 24 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
              <h1 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: "var(--text-primary)" }}>Run Payroll</h1>
              <span style={{
                padding: "4px 12px",
                borderRadius: "9999px",
                background: "rgba(134,96,248,0.2)",
                color: "var(--primary)",
                fontSize: 10,
                fontWeight: 500,
                border: "1px solid rgba(134,96,248,0.3)"
              }}>
                October 2023
              </span>
            </div>
            <p style={{ fontSize: 14, margin: 0, color: "var(--text-muted)" }}>Process salary for 842 active employees. Review, adjust, and execute payroll run.</p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <button className="btn-ghost" style={{ padding: "10px 20px" }}>
              <i className="fa-solid fa-file-import" style={{ marginRight: 8 }} /> Import Adjustments
            </button>
            <button className="btn-secondary" style={{ padding: "10px 20px" }}>
              <i className="fa-solid fa-eye" style={{ marginRight: 8 }} /> Preview Payroll
            </button>
            <button
              className="btn-primary"
              style={{
                padding: "12px 28px",
                animation: "pulse-glow 2s ease-in-out infinite",
                cursor: "pointer"
              }}
              onClick={handleRunPayroll}
            >
              <i className="fa-solid fa-play" style={{ marginRight: 8 }} /> Run Payroll
            </button>
          </div>
        </header>

        {/* Filter Bar */}
        <div style={{
          background: "var(--pay-surface)",
          border: "1px solid var(--border)",
          borderRadius: 16,
          padding: 16,
          display: "flex",
          flexWrap: "wrap",
          gap: 14
        }}>
          {/* Month */}
          <div className="field-wrap field-month" style={{ flex: "1 1 160px" }}>
            <i className="field-icon fa-regular fa-calendar" />
            <input
              type="month"
              className="universal-input"
              style={{ width: "100%", paddingLeft: "30px", cursor: "pointer" }}
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            />
          </div>
          {/* Branch */}
          <div className="field-wrap field-branch has-right-icon" style={{ flex: "1 1 150px" }}>
            <i className="field-icon fa-solid fa-building" />
            <select className="universal-input" style={{ width: "100%" }} value={selectedBranch} onChange={(e) => setSelectedBranch(e.target.value)}>
              <option>All Branches</option>
              <option>New York HQ</option>
              <option>London Office</option>
            </select>
            <i className="field-icon-right fa-solid fa-chevron-down" />
          </div>
          {/* Department */}
          <div className="field-wrap field-dept has-right-icon" style={{ flex: "1 1 150px" }}>
            <i className="field-icon fa-solid fa-users" />
            <select className="universal-input" style={{ width: "100%" }} value={selectedDepartment} onChange={(e) => setSelectedDepartment(e.target.value)}>
              <option>All Departments</option>
              <option>Engineering</option>
              <option>Sales</option>
              <option>Marketing</option>
            </select>
            <i className="field-icon-right fa-solid fa-chevron-down" />
          </div>
          {/* Type */}
          <div className="field-wrap field-type has-right-icon" style={{ flex: "1 1 150px" }}>
            <i className="field-icon fa-solid fa-list-ul" />
            <select className="universal-input" style={{ width: "100%" }} value={selectedType} onChange={(e) => setSelectedType(e.target.value)}>
              <option>All Types</option>
              <option>Full-Time</option>
              <option>Contractor</option>
            </select>
            <i className="field-icon-right fa-solid fa-chevron-down" />
          </div>
          {/* Pay Period */}
          <div className="field-wrap field-period has-right-icon" style={{ flex: "1 1 150px" }}>
            <i className="field-icon fa-regular fa-clock" />
            <select className="universal-input" style={{ width: "100%" }} value={selectedPayPeriod} onChange={(e) => setSelectedPayPeriod(e.target.value)}>
              <option>Monthly</option>
              <option>Weekly</option>
              <option>Bi-weekly</option>
            </select>
            <i className="field-icon-right fa-solid fa-chevron-down" />
          </div>
          {/* Search */}
          <div className="field-wrap field-search" style={{ flex: "2 1 250px" }}>
            <i className="field-icon fa-solid fa-magnifying-glass" />
            <input
              type="text"
              placeholder="Search by Employee ID or Name"
              className="universal-input"
              style={{ width: "100%" }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* KPI Summary Cards */}
        <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 18 }}>
          {kpiData.map((k, i) => (
            <div
              key={i}
              className="glass-card"
              style={{
                padding: 18,
                position: "relative",
                overflow: "hidden",
                transition: "all 0.25s ease-in-out",
                cursor: "pointer",
                transform: "translateY(0)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = "0 12px 24px rgba(0,0,0,0.15)";
                e.currentTarget.style.borderColor = "var(--primary)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "";
                e.currentTarget.style.borderColor = "";
              }}
            >
              <div style={{
                position: "absolute",
                right: -20,
                top: -20,
                width: 90,
                height: 90,
                background: k.glow,
                borderRadius: "50%",
                filter: "blur(22px)",
                transition: "all 0.3s ease-in-out",
              }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 500, color: "var(--text-muted)" }}>
                  <i className={k.icon} style={{ color: k.iconColor, fontSize: 12 }} /> {k.label}
                </div>
                <span className={k.bc} style={{ fontSize: 9, padding: "2px 8px", borderRadius: "9999px" }}>{k.badge}</span>
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--text-primary)" }}>{k.value}</div>
              <div style={{ fontSize: 10, marginTop: 4, color: "var(--text-muted)" }}>{k.sub}</div>
            </div>
          ))}
        </section>

        {/* Pre-Run Checklist */}
        <div className="glass-card" style={{ padding: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0, color: "var(--text-primary)" }}>Pre-Run Checklist</h3>
              <p style={{ fontSize: 12, margin: "4px 0 0", color: "var(--text-muted)" }}>Mandatory validations before payroll execution</p>
            </div>
            <span style={{ fontSize: 12, color: "var(--color-success)", display: "flex", alignItems: "center", gap: 8 }}>
              <i className="fa-solid fa-check-circle"></i> 5 of 6 Complete
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {checklistData.map((item, idx) => (
              <div
                key={idx}
                className="glass-elevated"
                style={{
                  padding: 16,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderLeft: `4px solid ${item.type === "success" ? "var(--color-success)" : "var(--color-warning)"}`,
                  transition: "all 0.2s ease"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <div style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: item.type === "success" ? "rgba(74,222,128,0.1)" : "rgba(245,158,11,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}>
                    <i className={item.icon} style={{ color: item.type === "success" ? "var(--color-success)" : "var(--color-warning)", fontSize: 16 }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 500, color: "var(--text-primary)", fontSize: 14 }}>{item.title}</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{item.sub}</div>
                  </div>
                </div>
                {item.hasButton ? (
                  <button className="btn-secondary" style={{ fontSize: 12, padding: "6px 16px" }}>{item.status}</button>
                ) : (
                  <span className="status-chip-success" style={{ fontSize: 11, padding: "4px 12px" }}>{item.status}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Exception Center */}
        <div className="glass-card" style={{ padding: 24, borderLeft: "4px solid var(--color-danger)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0, color: "var(--text-primary)" }}>Exception Center</h3>
                <span style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: "rgba(239,68,68,0.2)",
                  color: "var(--color-danger)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontWeight: 700
                }}>14</span>
              </div>
              <p style={{ fontSize: 12, margin: 0, color: "var(--text-muted)" }}>Issues blocking payroll execution - immediate action required</p>
            </div>
            <button className="btn-ghost" style={{ fontSize: 12, border: "1px solid var(--border)", padding: "6px 12px" }}>View All</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 16 }}>
            {exceptionData.map((item, idx) => (
              <div key={idx} className="glass-elevated" style={{ padding: 16, borderLeft: `2px solid ${item.color}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                  <div style={{ width: 32, height: 32, borderRadius: "50%", background: item.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <i className={item.icon} style={{ color: item.color, fontSize: 14 }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 500, color: "var(--text-primary)", fontSize: 13 }}>{item.title}</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{item.count}</div>
                  </div>
                </div>
                <button className="w-100 btn-secondary" style={{ width: "100%", fontSize: 12, padding: "6px" }}>Review Details</button>
              </div>
            ))}
          </div>
        </div>

        {/* Employee Payroll Queue Table */}
        <div className="glass-card" style={{ overflow: "hidden" }}>
          <div style={{
            padding: "20px 24px",
            borderBottom: "1px solid var(--border)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 16
          }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0, color: "var(--text-primary)" }}>Employee Payroll Queue</h3>
              <p style={{ fontSize: 12, margin: "4px 0 0", color: "var(--text-muted)" }}>Individual salary processing status and controls</p>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn-ghost" style={{ fontSize: 13, padding: "6px 14px", border: "1px solid var(--border)" }}>
                <i className="fa-solid fa-check-double" style={{ marginRight: 8 }} /> Bulk Approve
              </button>
              <button className="btn-ghost" style={{ fontSize: 13, padding: "6px 14px", border: "1px solid var(--border)" }}>
                <i className="fa-solid fa-file-excel" style={{ marginRight: 8 }} /> Export Excel
              </button>
              <button className="btn-ghost" style={{ fontSize: 13, padding: "6px 14px", border: "1px solid var(--border)" }}>
                <i className="fa-solid fa-filter" style={{ marginRight: 8 }} /> Filter
              </button>
            </div>
          </div>

        <div style={{ overflowX: "auto" }}>
  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, whiteSpace: "nowrap" }}>
    <thead>
      <tr style={{ background: "var(--pay-surface-2)" }}>
        <th style={{ padding: "10px 12px", width: 40 }}>
          <input type="checkbox" style={{ width: 14, height: 14 }} onChange={handleSelectAll} checked={selectedEmployees.length === employeeRows.length && employeeRows.length > 0} />
        </th>
        <th style={{ padding: "10px 12px", fontWeight: 600, fontSize: 10, textTransform: "uppercase", color: "var(--text-muted)" }}>Employee</th>
        <th style={{ padding: "10px 12px", fontWeight: 600, fontSize: 10, textTransform: "uppercase", color: "var(--text-muted)" }}>ID & Dept</th>
        <th style={{ padding: "10px 12px", fontWeight: 600, fontSize: 10, textTransform: "uppercase", color: "var(--text-muted)" }}>Basic</th>
        <th style={{ padding: "10px 12px", fontWeight: 600, fontSize: 10, textTransform: "uppercase", color: "var(--text-muted)" }}>Allowances</th>
        <th style={{ padding: "10px 12px", fontWeight: 600, fontSize: 10, textTransform: "uppercase", color: "var(--text-muted)" }}>Gross</th>
        <th style={{ padding: "10px 12px", fontWeight: 600, fontSize: 10, textTransform: "uppercase", color: "var(--text-muted)" }}>Deductions</th>
        <th style={{ padding: "10px 12px", fontWeight: 600, fontSize: 10, textTransform: "uppercase", color: "var(--text-muted)" }}>Net</th>
        <th style={{ padding: "10px 12px", fontWeight: 600, fontSize: 10, textTransform: "uppercase", color: "var(--text-muted)" }}>Status</th>
        <th style={{ padding: "10px 12px", fontWeight: 600, fontSize: 10, textTransform: "uppercase", color: "var(--text-muted)", textAlign: "right" }}>Actions</th>
      </tr>
    </thead>
    <tbody>
      {employeeRows.map((row, idx) => (
        <tr key={idx} style={{ borderBottom: "1px solid var(--border)" }}>
          <td style={{ padding: "8px 12px" }}>
            <input type="checkbox" style={{ width: 14, height: 14 }} checked={selectedEmployees.includes(row.id)} onChange={() => handleEmployeeSelect(row.id)} />
          </td>
          <td style={{ padding: "8px 12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <img src={row.avatar} alt={row.name} style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }} />
              <div>
                <div style={{ fontWeight: 500, fontSize: 12 }}>{row.name}</div>
                <div style={{ fontSize: 10, color: "var(--text-muted)" }}>{row.role}</div>
              </div>
            </div>
           </td>
          <td style={{ padding: "8px 12px" }}>
            <div style={{ fontWeight: 500, fontSize: 12 }}>{row.id}</div>
            <div style={{ fontSize: 10, color: "var(--text-muted)" }}>{row.dept}</div>
           </td>
          <td style={{ padding: "8px 12px", fontWeight: 500, fontSize: 12 }}>{row.basic}</td>
          <td style={{ padding: "8px 12px", color: "var(--color-success)", fontSize: 12 }}>{row.allowances}</td>
          <td style={{ padding: "8px 12px", fontWeight: 700, fontSize: 12 }}>{row.gross}</td>
          <td style={{ padding: "8px 12px", color: "var(--color-danger)", fontSize: 12 }}>{row.deductions}</td>
          <td style={{ padding: "8px 12px", fontWeight: 700, fontSize: 12 }}>{row.net}</td>
          <td style={{ padding: "8px 12px" }}>
            <span className={`status-chip-${row.statusType}`} style={{ fontSize: 10, padding: "3px 8px" }}>{row.status}</span>
           </td>
          <td style={{ padding: "8px 12px", textAlign: "right" }}>
            <button className="btn-ghost" style={{ padding: "0 4px" }}><i className="fa-solid fa-eye" style={{ fontSize: 11 }}></i></button>
            <button className="btn-ghost" style={{ padding: "0 4px" }}><i className="fa-solid fa-pen-to-square" style={{ fontSize: 11 }}></i></button>
            <button className="btn-ghost" style={{ padding: "0 4px" }}><i className="fa-solid fa-ellipsis-vertical" style={{ fontSize: 11 }}></i></button>
           </td>
         </tr>
      ))}
    </tbody>
  </table>
</div>

          {/* Pagination */}
          <div style={{
            padding: "10px 16px",
            borderTop: "1px solid var(--border)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: 11,
            color: "var(--text-muted)"
          }}>
            <span>Showing 1-5 of 842 employees</span>
            <div style={{ display: "flex", gap: 4 }}>
              <button className="page-btn" style={{ width: 28, height: 28, borderRadius: 4, background: "var(--pay-surface-2)", border: "1px solid var(--border)", cursor: "pointer", fontSize: 11 }}><i className="fa-solid fa-chevron-left" style={{ fontSize: 10 }}></i></button>
              <button className="page-btn active" style={{ width: 28, height: 28, borderRadius: 4, background: "var(--primary)", color: "#fff", border: "none", cursor: "pointer", fontSize: 11 }}>1</button>
              <button className="page-btn" style={{ width: 28, height: 28, borderRadius: 4, background: "var(--pay-surface-2)", border: "1px solid var(--border)", cursor: "pointer", fontSize: 11 }}>2</button>
              <button className="page-btn" style={{ width: 28, height: 28, borderRadius: 4, background: "var(--pay-surface-2)", border: "1px solid var(--border)", cursor: "pointer", fontSize: 11 }}>3</button>
              <button className="page-btn" style={{ width: 28, height: 28, borderRadius: 4, background: "var(--pay-surface-2)", border: "1px solid var(--border)", cursor: "pointer", fontSize: 11 }}>...</button>
              <button className="page-btn" style={{ width: 28, height: 28, borderRadius: 4, background: "var(--pay-surface-2)", border: "1px solid var(--border)", cursor: "pointer", fontSize: 11 }}>169</button>
              <button className="page-btn" style={{ width: 28, height: 28, borderRadius: 4, background: "var(--pay-surface-2)", border: "1px solid var(--border)", cursor: "pointer", fontSize: 11 }}><i className="fa-solid fa-chevron-right" style={{ fontSize: 10 }}></i></button>
            </div>
          </div>
        </div>

        {/* Smart Insights */}
        <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))", gap: 16 }}>
          {insightData.map((item, idx) => (
            <div
              key={idx}
              className="glass-card"
              style={{
                padding: 20,
                display: "flex",
                alignItems: "flex-start",
                gap: 16,
                cursor: "pointer",
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--primary)";
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <div style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: item.grad,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                boxShadow: `0 4px 12px ${item.shadow}`
              }}>
                <i className={item.icon} style={{ color: "#fff", fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontSize: 11, marginBottom: 4, color: "var(--text-muted)" }}>{item.label}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)" }}>{item.value}</div>
                <div style={{ fontSize: 11, marginTop: 4, color: item.trendColor, display: "flex", alignItems: "center", gap: 4 }}>{item.trend}</div>
              </div>
            </div>
          ))}
        </section>

        {/* Post Run Actions - Success Message */}
        <section
          id="post-run-actions"
          className="glass-card"
          style={{
            padding: 24,
            borderLeft: "4px solid var(--color-success)",
            display: showPostRun ? "block" : "none",
            animation: "fadeInUp 0.5s ease-out"
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: 24, marginBottom: 24 }}>
            <div style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "rgba(74,222,128,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0
            }}>
              <i className="fa-solid fa-check-circle" style={{ color: "var(--color-success)", fontSize: 32 }} />
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, color: "var(--text-primary)" }}>Payroll Run Successful!</h3>
              <p style={{ color: "var(--text-muted)", fontSize: 14, marginBottom: 16 }}>October 2023 payroll processed for 842 employees. Total disbursement: ₹1,24,50,000</p>
              <div style={{ display: "flex", gap: 16, fontSize: 12 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--color-success)" }}><i className="fa-solid fa-check"></i> 828 Approved</span>
                <span style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--color-warning)" }}><i className="fa-solid fa-clock"></i> 14 Pending</span>
                <span style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--color-danger)" }}><i className="fa-solid fa-xmark"></i> 0 Failed</span>
              </div>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12 }}>
            <button className="btn-primary" style={{ padding: "12px" }}><i className="fa-solid fa-file-invoice" style={{ marginRight: 8 }}></i>View Payroll Register</button>
            <button className="btn-secondary" style={{ padding: "12px" }}><i className="fa-solid fa-building-columns" style={{ marginRight: 8 }}></i>Generate Bank File</button>
            <button className="btn-secondary" style={{ padding: "12px" }}><i className="fa-solid fa-download" style={{ marginRight: 8 }}></i>Download Summary</button>
            <button className="btn-ghost" style={{ padding: "12px", border: "1px solid var(--border)" }}><i className="fa-solid fa-envelope" style={{ marginRight: 8 }}></i>Send Payslips</button>
          </div>
        </section>

      </main>

      {/* Add global animation keyframes */}
      <style>{`
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 15px rgba(134, 96, 248, 0.3); }
          50% { box-shadow: 0 0 25px rgba(134, 96, 248, 0.5); }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .glass-card {
          background: var(--pay-surface);
          border: 1px solid var(--pay-border);
          border-radius: 16px;
          transition: all 0.2s ease;
        }
        
        .glass-elevated {
          background: var(--pay-surface-2);
          border: 1px solid var(--pay-border);
          border-radius: 12px;
          transition: all 0.2s ease;
        }
        
        .btn-primary {
          background: var(--primary);
          color: #fff;
          border: none;
          border-radius: 24px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        
        .btn-primary:hover {
          background: var(--primary-hover);
          transform: translateY(-1px);
          box-shadow: 0 6px 25px rgba(134, 96, 248, 0.5);
        }
        
        .btn-secondary {
          background: rgba(169, 163, 201, 0.1);
          color: #fff;
          border: 1px solid rgba(169, 163, 201, 0.2);
          border-radius: 24px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        
        .btn-secondary:hover {
          background: rgba(169, 163, 201, 0.2);
          border-color: rgba(169, 163, 201, 0.3);
        }
        
        .btn-ghost {
          background: transparent;
          color: var(--text-muted);
          border: none;
          border-radius: 24px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
        }
        
        .btn-ghost:hover {
          color: var(--text-primary);
          background: rgba(255, 255, 255, 0.05);
        }

        /* ── Light theme overrides ── */
        .light .glass-card {
          background: #ffffff !important;
          border-color: #e5e7eb !important;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06) !important;
        }

        .light .glass-elevated {
          background: #f9fafb !important;
          border-color: #e5e7eb !important;
        }

        .light .btn-secondary {
          color: #1A1A2E !important;
          background: rgba(134,96,248,0.06) !important;
          border-color: rgba(134,96,248,0.2) !important;
        }

        .light .btn-secondary:hover {
          background: rgba(134,96,248,0.12) !important;
        }

        .light .btn-ghost {
          color: #6B6B8A !important;
        }

        .light .btn-ghost:hover {
          background: rgba(134,96,248,0.06) !important;
          color: #1A1A2E !important;
        }

        .light .universal-input {
          background: #ffffff !important;
          border-color: #E0E0F0 !important;
          color: #1A1A2E !important;
        }

        .light .page-btn {
          background: #f9fafb !important;
          color: #6B6B8A !important;
          border-color: #E0E0F0 !important;
        }

        .light .page-btn:hover {
          background: #E9ECF5 !important;
          color: #1A1A2E !important;
        }
        
        .status-chip-success {
          background: rgba(74, 222, 128, 0.1);
          color: var(--color-success);
          border: 1px solid rgba(74, 222, 128, 0.2);
          border-radius: 20px;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
        }
        
        .status-chip-warning {
          background: rgba(245, 158, 11, 0.1);
          color: var(--color-warning);
          border: 1px solid rgba(245, 158, 11, 0.2);
          border-radius: 20px;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
        }
        
        .status-chip-error {
          background: rgba(239, 68, 68, 0.1);
          color: var(--color-danger);
          border: 1px solid rgba(239, 68, 68, 0.2);
          border-radius: 20px;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
        }
        
        .status-chip-accent {
          background: rgba(134, 96, 248, 0.1);
          color: var(--primary);
          border: 1px solid rgba(134, 96, 248, 0.2);
          border-radius: 20px;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
        }
        
        .table-row-hover:hover {
          background: rgba(134, 96, 248, 0.05);
        }
        
        .universal-input {
          background: var(--input-bg);
          border: 1px solid var(--input-border);
          border-radius: 8px;
          padding: 8px 12px;
          color: var(--input-color);
          font-size: 13px;
          font-family: var(--font);
          outline: none;
          transition: all 0.2s ease;
        }
        
        .universal-input:focus {
          border-color: var(--input-focus-border);
          box-shadow: 0 0 0 3px var(--input-focus-shadow);
        }
        
        .w-100 {
          width: 100%;
        }
        
        .page-btn {
          transition: all 0.2s ease;
        }
        
        .page-btn:hover {
          background: var(--border);
          color: var(--text-primary);
        }
        
        .page-btn.active {
          background: var(--primary) !important;
          color: #fff !important;
          border: none !important;
        }
      `}</style>
    </div>
  );
};

export default RunPayroll;